import React, { useState, useEffect, useCallback } from 'react';
import StandardLayout from '../../../components/layout/StandardLayout';
import { getExams, getExamQuestions, addQuestionToExam, removeQuestionFromExam, updateQuestionPosition } from '../../../services/examAPIService';
import { loadQuestionsWithCategoriesAdmin } from '../../../services/questionAPIService';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAlert } from '../../../context/AlertContext';

const ExamQuestionMapping = () => {
    const [exams, setExams] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [selectedExam, setSelectedExam] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [mappedQuestions, setMappedQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { showAlert } = useAlert();

    const loadMappedQuestions = useCallback(async () => {
        if (!selectedSubcategory) return;
        try {
            const mappedData = await getExamQuestions(selectedSubcategory);
            setMappedQuestions(mappedData);
        } catch (err) {
            console.error('Error loading mapped questions:', err);
            showAlert('Fehler beim Laden der zugeordneten Fragen', 'error');
        }
    }, [selectedSubcategory, showAlert]);

    const loadInitialData = useCallback(async () => {
        try {
            setLoading(true);
            const [examsData, questionsData] = await Promise.all([
                getExams(),
                loadQuestionsWithCategoriesAdmin()
            ]);
            setExams(examsData);
            setQuestions(questionsData);
            setError(null);
        } catch (err) {
            setError('Fehler beim Laden der Daten');
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    useEffect(() => {
        loadMappedQuestions();
    }, [loadMappedQuestions]);

    const handleExamChange = (e) => {
        const examId = e.target.value;
        setSelectedExam(examId);
        setSelectedSubcategory('');
        setMappedQuestions([]);
    };

    const handleSubcategoryChange = (e) => {
        setSelectedSubcategory(e.target.value);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
    };

    const handleQuestionToggle = async (questionId) => {
        try {
            const isCurrentlyMapped = mappedQuestions.some(mq => mq.question_id === questionId);
            
            if (isCurrentlyMapped) {
                const mapping = mappedQuestions.find(mq => mq.question_id === questionId);
                await removeQuestionFromExam(mapping.id);
                setMappedQuestions(prev => prev.filter(mq => mq.question_id !== questionId));
                showAlert('Frage wurde erfolgreich entfernt', 'success');
            } else {
                const newMapping = await addQuestionToExam({
                    question_id: questionId,
                    subcategory_id: parseInt(selectedSubcategory),
                    position: mappedQuestions.length + 1
                });
                setMappedQuestions(prev => [...prev, newMapping]);
                showAlert('Frage wurde erfolgreich hinzugefügt', 'success');
            }
        } catch (err) {
            console.error('Error toggling question:', err);
            showAlert('Fehler beim Aktualisieren der Zuordnung', 'error');
        }
    };

    const handlePositionChange = async (mappingId, newPosition) => {
        try {
            await updateQuestionPosition(mappingId, newPosition);
            await loadMappedQuestions(); // Neu laden um die aktualisierten Positionen zu erhalten
            showAlert('Position wurde erfolgreich aktualisiert', 'success');
        } catch (err) {
            console.error('Error updating position:', err);
            showAlert('Fehler beim Aktualisieren der Position', 'error');
        }
    };

    const filteredQuestions = questions.filter(question => 
        question.question_text.toLowerCase().includes(searchQuery)
    );

    const selectedExamData = exams.find(exam => exam.id === parseInt(selectedExam));

    if (loading) {
        return (
            <StandardLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </StandardLayout>
        );
    }

    return (
        <StandardLayout>
            <div className="container mx-auto px-4 py-8">
                <h1 className="text-2xl font-bold mb-6">Fragen zu Prüfungsteilen zuordnen</h1>

                {error && (
                    <div className="alert alert-error mb-4">
                        <XMarkIcon className="w-6 h-6" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6 mb-6">
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Prüfung auswählen</span>
                        </label>
                        <select
                            value={selectedExam}
                            onChange={handleExamChange}
                            className="select select-bordered w-full"
                        >
                            <option value="">Prüfung auswählen</option>
                            {exams.map(exam => (
                                <option key={exam.id} value={exam.id}>
                                    {exam.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedExam && (
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Prüfungsteil auswählen</span>
                            </label>
                            <select
                                value={selectedSubcategory}
                                onChange={handleSubcategoryChange}
                                className="select select-bordered w-full"
                            >
                                <option value="">Prüfungsteil auswählen</option>
                                {selectedExamData?.subcategories.map(subcategory => (
                                    <option key={subcategory.id} value={subcategory.id}>
                                        {subcategory.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {selectedSubcategory && (
                        <>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Fragen suchen</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Fragen durchsuchen..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        className="input input-bordered w-full pr-10"
                                    />
                                    <MagnifyingGlassIcon className="w-5 h-5 absolute right-3 top-3 text-gray-400" />
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="table w-full">
                                    <thead>
                                        <tr>
                                            <th>Auswählen</th>
                                            <th>ID</th>
                                            <th>Frage</th>
                                            <th>Position</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredQuestions.map(question => {
                                            const mapping = mappedQuestions.find(mq => mq.question_id === question.id);
                                            return (
                                                <tr key={question.id} className="hover">
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox"
                                                            checked={!!mapping}
                                                            onChange={() => handleQuestionToggle(question.id)}
                                                        />
                                                    </td>
                                                    <td>{question.id}</td>
                                                    <td>{question.question_text}</td>
                                                    <td>
                                                        {mapping && (
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={mapping.position || ''}
                                                                onChange={(e) => handlePositionChange(mapping.id, parseInt(e.target.value))}
                                                                className="input input-bordered w-24"
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </StandardLayout>
    );
};

export default ExamQuestionMapping; 